import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import Index from "./pages/Index.tsx";
import { lazy, Suspense, ReactNode } from "react";

// Portal routes are lazy-loaded so the public landing ships a minimal bundle.
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const PortalShell = lazy(() => import("./components/portal/PortalShell.tsx"));
const Login = lazy(() => import("./pages/portal/Login.tsx"));
const Dashboard = lazy(() => import("./pages/portal/Dashboard.tsx"));
const Sites = lazy(() => import("./pages/portal/Sites.tsx"));
const SiteDetail = lazy(() => import("./pages/portal/SiteDetail.tsx"));
const Logs = lazy(() => import("./pages/portal/Logs.tsx"));
const Users = lazy(() => import("./pages/portal/Users.tsx"));
const Lockouts = lazy(() => import("./pages/portal/Lockouts.tsx"));
const Permits = lazy(() => import("./pages/portal/Permits.tsx"));
const Organizations = lazy(() => import("./pages/portal/Organizations.tsx"));
const Shifts = lazy(() => import("./pages/portal/Shifts.tsx"));
const WorkOrders = lazy(() => import("./pages/portal/WorkOrders.tsx"));
const Checklists = lazy(() => import("./pages/portal/Checklists.tsx"));
const Procedures = lazy(() => import("./pages/portal/Procedures.tsx"));
const Incidents = lazy(() => import("./pages/portal/Incidents.tsx"));
const Audits = lazy(() => import("./pages/portal/Audits.tsx"));
const Reports = lazy(() => import("./pages/portal/Reports.tsx"));
const ProcessFlow = lazy(() => import("./pages/portal/ProcessFlow.tsx"));
const Monitoring = lazy(() => import("./pages/portal/Monitoring.tsx"));
const Certificates = lazy(() => import("./pages/portal/Certificates.tsx"));
const Compliance = lazy(() => import("./pages/portal/Compliance.tsx"));
const Documents = lazy(() => import("./pages/portal/Documents.tsx"));
const PitchDeck = lazy(() => import("./pages/portal/PitchDeck.tsx"));
const PitchGenerator = lazy(() => import("./pages/portal/PitchGenerator.tsx"));
const DemoMode = lazy(() => import("./pages/portal/DemoMode.tsx"));
const AuditTrail = lazy(() => import("./pages/portal/AuditTrail.tsx"));
const Analytics = lazy(() => import("./pages/portal/Analytics.tsx"));
const Workers = lazy(() => import("./pages/portal/Workers.tsx"));
const Valuation = lazy(() => import("./pages/portal/Valuation.tsx"));
const EnergyIsolation = lazy(() => import("./pages/portal/EnergyIsolation.tsx"));
const DigitalTwin = lazy(() => import("./pages/portal/DigitalTwin.tsx"));
const IoTPlatform = lazy(() => import("./pages/portal/IoTPlatform.tsx"));
const AICopilot = lazy(() => import("./pages/portal/AICopilot.tsx"));
const VideoAnalytics = lazy(() => import("./pages/portal/VideoAnalytics.tsx"));
const Trackmin = lazy(() => import("./pages/portal/Trackmin.tsx"));
const Biometrics = lazy(() => import("./pages/portal/Biometrics.tsx"));
const Integrations = lazy(() => import("./pages/portal/Integrations.tsx"));
const Equipment = lazy(() => import("./pages/portal/Equipment.tsx"));
const PermitToWork = lazy(() => import("./pages/portal/PermitToWork.tsx"));
const AIAgents = lazy(() => import("./pages/portal/AIAgents.tsx"));
const Ecosystem = lazy(() => import("./pages/portal/Ecosystem.tsx"));
const License = lazy(() => import("./pages/portal/License.tsx"));
const AdminLicenses = lazy(() => import("./pages/portal/AdminLicenses.tsx"));
const FleetHealth = lazy(() => import("./pages/portal/FleetHealth.tsx"));
const ComplianceFrameworks = lazy(() => import("./pages/portal/ComplianceFrameworks.tsx"));
const RolesPermissions = lazy(() => import("./pages/portal/RolesPermissions.tsx"));
const Security = lazy(() => import("./pages/legal/Security.tsx"));
const Privacy = lazy(() => import("./pages/legal/Privacy.tsx"));
const Terms = lazy(() => import("./pages/legal/Terms.tsx"));
const Subprocessors = lazy(() => import("./pages/legal/Subprocessors.tsx"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="h-8 w-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteFallback />;
  if (!user) return <Navigate to="/portal/login" replace />;
  return <PortalShell>{children}</PortalShell>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/portal/login" element={<Login />} />
              <Route path="/portal" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/portal/lockout" element={<ProtectedRoute><Lockouts /></ProtectedRoute>} />
              <Route path="/portal/permits" element={<ProtectedRoute><Permits /></ProtectedRoute>} />
              <Route path="/portal/work-orders" element={<ProtectedRoute><WorkOrders /></ProtectedRoute>} />
              <Route path="/portal/checklists" element={<ProtectedRoute><Checklists /></ProtectedRoute>} />
              <Route path="/portal/shifts" element={<ProtectedRoute><Shifts /></ProtectedRoute>} />
              <Route path="/portal/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
              <Route path="/portal/procedures" element={<ProtectedRoute><Procedures /></ProtectedRoute>} />
              <Route path="/portal/audits" element={<ProtectedRoute><Audits /></ProtectedRoute>} />
              <Route path="/portal/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
              <Route path="/portal/process" element={<ProtectedRoute><ProcessFlow /></ProtectedRoute>} />
              <Route path="/portal/process/:id" element={<ProtectedRoute><ProcessFlow /></ProtectedRoute>} />
              <Route path="/portal/monitoring" element={<ProtectedRoute><Monitoring /></ProtectedRoute>} />
              <Route path="/portal/certificates" element={<ProtectedRoute><Certificates /></ProtectedRoute>} />
              <Route path="/portal/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
              <Route path="/portal/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
              <Route path="/portal/sites/:id" element={<ProtectedRoute><SiteDetail /></ProtectedRoute>} />
              <Route path="/portal/logs" element={<ProtectedRoute><Logs /></ProtectedRoute>} />
              <Route path="/portal/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
              <Route path="/portal/organizations" element={<ProtectedRoute><Organizations /></ProtectedRoute>} />
              <Route path="/portal/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
              <Route path="/portal/pitch" element={<ProtectedRoute><PitchDeck /></ProtectedRoute>} />
              <Route path="/portal/pitch-generator" element={<ProtectedRoute><PitchGenerator /></ProtectedRoute>} />
              <Route path="/portal/demo" element={<DemoMode />} />
              <Route path="/portal/audit-trail" element={<ProtectedRoute><AuditTrail /></ProtectedRoute>} />
              <Route path="/portal/command-center" element={<Navigate to="/portal" replace />} />
              <Route path="/portal/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
              <Route path="/portal/workers" element={<ProtectedRoute><Workers /></ProtectedRoute>} />
              <Route path="/portal/valuation" element={<ProtectedRoute><Valuation /></ProtectedRoute>} />
              <Route path="/portal/energy-isolation" element={<ProtectedRoute><EnergyIsolation /></ProtectedRoute>} />
              <Route path="/portal/digital-twin" element={<ProtectedRoute><DigitalTwin /></ProtectedRoute>} />
              <Route path="/portal/iot" element={<ProtectedRoute><IoTPlatform /></ProtectedRoute>} />
              <Route path="/portal/ai-copilot" element={<ProtectedRoute><AICopilot /></ProtectedRoute>} />
              <Route path="/portal/video-analytics" element={<ProtectedRoute><VideoAnalytics /></ProtectedRoute>} />
              <Route path="/portal/trackmin" element={<ProtectedRoute><Trackmin /></ProtectedRoute>} />
              <Route path="/portal/biometrics" element={<ProtectedRoute><Biometrics /></ProtectedRoute>} />
              <Route path="/portal/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
              <Route path="/portal/equipment" element={<ProtectedRoute><Equipment /></ProtectedRoute>} />
              <Route path="/portal/ptw" element={<ProtectedRoute><PermitToWork /></ProtectedRoute>} />
              <Route path="/portal/ai-agents" element={<ProtectedRoute><AIAgents /></ProtectedRoute>} />
              <Route path="/portal/ecosystem" element={<ProtectedRoute><Ecosystem /></ProtectedRoute>} />
              <Route path="/portal/war-room" element={<Navigate to="/portal" replace />} />
              <Route path="/portal/licencia" element={<ProtectedRoute><License /></ProtectedRoute>} />
              <Route path="/portal/admin/licencias" element={<ProtectedRoute><AdminLicenses /></ProtectedRoute>} />
              <Route path="/portal/salud-flota" element={<ProtectedRoute><FleetHealth /></ProtectedRoute>} />
              <Route path="/portal/normativa" element={<ProtectedRoute><ComplianceFrameworks /></ProtectedRoute>} />
              <Route path="/portal/roles" element={<ProtectedRoute><RolesPermissions /></ProtectedRoute>} />
              <Route path="/security" element={<Security />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/subprocessors" element={<Subprocessors />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
